import { useEffect, useState } from "react";
import { supabase } from "@vinoga/db";
import { Save, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";

const sectionClass = "bg-card border border-border rounded-lg p-5 space-y-4";

type Group = {
  id: string;
  code: string;
  name: string;
  price_list_id: string;
  vat_display: "inc" | "exc";
  is_business: boolean;
};

type PriceList = { id: string; code: string; name: string };

export function CustomerGroups() {
  const { toast } = useToast();
  const t = useT();

  const [groups, setGroups] = useState<Group[]>([]);
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [g, pl] = await Promise.all([
        supabase.from("customer_groups").select("*").order("code"),
        supabase.from("price_lists").select("*").order("position"),
      ]);
      if (cancelled) return;
      setGroups((g.data ?? []) as Group[]);
      setPriceLists((pl.data ?? []) as PriceList[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateGroup = (id: string, patch: Partial<Group>) => {
    setGroups((prev) => prev.map((g) => (g.id === id ? { ...g, ...patch } : g)));
  };

  const saveGroup = async (group: Group) => {
    setSavingId(group.id);
    const { error } = await supabase
      .from("customer_groups")
      .update({
        name: group.name,
        price_list_id: group.price_list_id,
        vat_display: group.vat_display,
      })
      .eq("id", group.id);
    if (error) {
      toast({
        title: t("common.saveFailed", "Save failed"),
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({ title: t("customerGroups.saved", "Group saved") });
    }
    setSavingId(null);
  };

  if (loading) {
    return (
      <div className="p-4 font-body text-sm text-muted-foreground">
        {t("common.loading", "Loading...")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-xl font-bold text-foreground">
          {t("customerGroups.title", "Customer Groups")}
        </h1>
        <p className="font-body text-xs text-muted-foreground">
          {t(
            "customerGroups.subtitle",
            "Three foundational groups (B2C + B2B-A + B2B-B) wired to the price-list system. Code is read-only — used by the storefront and integrations."
          )}
        </p>
      </div>

      <div className="space-y-4">
        {groups.map((g) => (
          <section key={g.id} className={sectionClass}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users size={18} className="text-muted-foreground" />
                <div>
                  <h2 className="font-display text-sm font-bold text-foreground">
                    {g.name}
                  </h2>
                  <p className="font-mono text-xs text-muted-foreground">{g.code}</p>
                </div>
                {g.is_business && (
                  <Badge variant="info">{t("customerGroups.business", "B2B")}</Badge>
                )}
              </div>
              <Button
                size="sm"
                onClick={() => saveGroup(g)}
                disabled={savingId === g.id}
              >
                <Save size={14} />
                {savingId === g.id
                  ? t("common.saving", "Saving…")
                  : t("common.save", "Save")}
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="mb-1 block font-body text-xs text-muted-foreground">
                  {t("customerGroups.name", "Display name")}
                </label>
                <Input
                  value={g.name}
                  onChange={(e) => updateGroup(g.id, { name: e.target.value })}
                  className="h-9"
                />
              </div>
              <div>
                <label className="mb-1 block font-body text-xs text-muted-foreground">
                  {t("customerGroups.priceList", "Price list")}
                </label>
                <Select
                  value={g.price_list_id}
                  onValueChange={(val) => updateGroup(g.id, { price_list_id: val })}
                >
                  <SelectTrigger className="h-9 font-body text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {priceLists.map((pl) => (
                      <SelectItem key={pl.id} value={pl.id}>
                        {pl.name}{" "}
                        <span className="font-mono text-muted-foreground">/{pl.code}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block font-body text-xs text-muted-foreground">
                  {t("customerGroups.vatDisplay", "VAT display")}
                </label>
                <Select
                  value={g.vat_display}
                  onValueChange={(val) =>
                    updateGroup(g.id, { vat_display: val as "inc" | "exc" })
                  }
                >
                  <SelectTrigger className="h-9 font-body text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inc">
                      {t("customerGroups.vatInc", "Including VAT")}
                    </SelectItem>
                    <SelectItem value="exc">
                      {t("customerGroups.vatExc", "Excluding VAT")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
